import React from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { InfoCard, StylovyzeTooltip } from '@innovyze/stylovyze';
import HelpIcon from '@mui/icons-material/Help';
import { InfoOutlined } from '@mui/icons-material';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { Content, Heading, Key } from './RiskNormalize.styles';
import { cardGap, cardSx } from './redesign/cardSx';

interface RiskNormalizeProps {
	isLoading: boolean;
	normalizeSettings: { useNormalize: boolean; normalizeValue: number };
	onNormalizeChanged(useNormalize: boolean, normalizeValue: number): void;
	redesigned?: boolean;
}

export const RiskNormalize = ({
	isLoading,
	normalizeSettings,
	onNormalizeChanged,
	redesigned = false,
}: RiskNormalizeProps) => {
	const { t } = useGlobalization();

	const contents = () => {
		return (
			<>
				<Heading>
					{t('What does it mean to Normalize Risk Scores?')}
				</Heading>
				<Content>
					<b>{t('Normalization Equation:')}</b>
					<br />
					{t('( PRS / HCRS ) * NV')}
					<br />
					<br />
					<Key>
						{t('    PRS = Pipe Risk Score')}
						<br />
						{t('    HCRS  = Highest Calculated Risk Score')}
						<br />
						{t('    NV = Normalization Value')}
					</Key>
					<br />
					<b>{t('Example:')}</b>
					<br />
					{t('Pipe Risk Score = 233')}
					<br />
					{t('Highest Calculated Risk Score = 545')}
					<br />
					{t('Normalization Value = 100')}
					<br />
					<br />
					{t('Normalized Score = (233/545)*100 = 42.75')}
					<br />
				</Content>
			</>
		);
	};

	const onCheckUseNormalization = () => {
		onNormalizeChanged(
			!normalizeSettings.useNormalize,
			normalizeSettings.normalizeValue,
		);
	};

	const onNormalizeValueChange = (value: number) => {
		onNormalizeChanged(normalizeSettings.useNormalize, value);
	};

	const cardTitle = () => {
		return (
			<Stack direction={'row'} alignItems={'center'}>
				{t('Normalize Risk Scores')}
				<StylovyzeTooltip title={contents()} padded>
					<HelpIcon
						color="secondary"
						style={{ marginLeft: '10px' }}
					/>
				</StylovyzeTooltip>
			</Stack>
		);
	};

	const controls = (
		<>
			<FormControlLabel
				disabled={isLoading}
				control={
					<Checkbox
						checked={normalizeSettings.useNormalize}
						onChange={onCheckUseNormalization}
					/>
				}
				label={
					redesigned ? (
						<Typography sx={{ fontSize: 14 }}>
							{t('Use Normalization 0 to')}
						</Typography>
					) : (
						t('Use Normalization 0 to')
					)
				}
			/>
			<TextField
				disabled={isLoading || !normalizeSettings.useNormalize}
				style={{
					width: '100px',
					verticalAlign: 'text-top',
				}}
				value={normalizeSettings.normalizeValue}
				onChange={e => {
					let value = parseFloat(e.target.value);
					if (!isNaN(value)) {
						if (value < 1) value = 1;

						value = Math.round(value * 100) / 100;
					}

					onNormalizeValueChange(value);
				}}
				onBlur={e => {
					let value = parseFloat(e.target.value);
					if (isNaN(value)) value = 1;
					onNormalizeValueChange(Math.round(value * 100) / 100);
				}}
				inputProps={{
					step: '1',
					min: 1,
				}}
				variant="standard"
				type="number"
				size="small"
			/>
		</>
	);

	if (redesigned) {
		return (
			<Paper elevation={0} sx={{ ...cardSx, mt: cardGap }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography
						sx={{
							fontSize: 16,
							fontWeight: 700,
							color: '#0d1520',
						}}>
						{t('Normalize Risk Scores')}
					</Typography>
					<StylovyzeTooltip title={contents()} padded>
						<InfoOutlined
							data-cy="normalize-help-icon"
							sx={{
								fontSize: 16,
								color: '#b3bac4',
								cursor: 'default',
							}}
						/>
					</StylovyzeTooltip>
				</Box>
				<Typography
					sx={{ fontSize: 13, color: '#5b6472', mt: 0.5, mb: 1.5 }}>
					{t(
						'Rescale risk scores to a fixed range, such as 0–100, to make them easier to read and compare. The highest risk score becomes the maximum value, and the risk grade thresholds adjust to the new range.',
					)}
				</Typography>
				{controls}
			</Paper>
		);
	}

	return <InfoCard title={cardTitle()}>{controls}</InfoCard>;
};
